// Read-only highlight rendering for the tutor's TSIA2 review.
//
// The student player's Highlighter without the painting half: a tutor never
// makes a highlight, so there is no menu, no pen glyph and no selection
// measuring here. What remains is the stored shape and the layer that redraws
// it.
//
// Offsets, not markup: each layer is regenerated against freshly rendered text
// via the shared `global/passageHighlight` helper, which is what makes a
// highlight saved months earlier still land on the right words.
import React, { useEffect, useState } from 'react';

import { generatePassageHTML } from 'global/passageHighlight';

export type HighlightColor = 'yellow' | 'green' | 'blue' | 'pink';

/** What the server stores and returns: offsets plus the target. */
export type TSIA2Annotation = {
    // null means the question stem rather than a passage.
    passageId: string | null;
    startIndex: number;
    endIndex: number;
    color: HighlightColor;
};

function overlayFor(element: Element | null, annotations: TSIA2Annotation[]) {
    if (!element) return [];
    return annotations.map((annotation, index) => ({
        key: `${annotation.startIndex}-${annotation.endIndex}-${index}`,
        color: annotation.color,
        html: generatePassageHTML(element, annotation.startIndex, annotation.endIndex).passageHTML,
    }));
}

/** One absolutely-positioned copy of the text per highlight, with only that
 *  highlight's span marked, stacked over the real text. Several colours can
 *  overlap without one rewriting another, and the text itself is untouched. */
export function HighlightLayers({
    target, annotations, layout,
}: {
    target: React.RefObject<HTMLElement>;
    annotations: TSIA2Annotation[];
    layout: number;
}) {
    const [overlays, setOverlays] = useState<ReturnType<typeof overlayFor>>([]);

    useEffect(() => {
        setOverlays(overlayFor(target.current, annotations));
    }, [target, annotations, layout]);

    return (
        <>
            {overlays.map(overlay => (
                <div
                    key={overlay.key}
                    className={`tsia2-overlay tsia2-overlay-${overlay.color}`}
                    aria-hidden='true'
                    dangerouslySetInnerHTML={{ __html: overlay.html }}
                />
            ))}
        </>
    );
}
